import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const InventoryContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = 'vrr_admin_token';

// Key helper: for variantless products use slug; for variants use slug|label
const keyFor = (slug, variantLabel) => variantLabel ? `${slug}|${variantLabel}` : slug;

const emptyRec = { stock: 0, status: 'IN_STOCK' };

export const InventoryProvider = ({ children }) => {
  const [invMap, setInvMap] = useState({});         // key -> { stock, status }
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/inventory`);
      if (!res.ok) throw new Error(`inventory fetch ${res.status}`);
      const data = await res.json();
      const map = {};
      for (const r of (data.items || [])) {
        const k = keyFor(r.product_slug, r.variant_label);
        map[k] = { stock: r.stock, status: r.status };
      }
      setInvMap(map);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[inventory] refresh failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const getStock = useCallback((slug, variantLabel) => {
    const rec = invMap[keyFor(slug, variantLabel)];
    return rec ? rec.stock : 0;
  }, [invMap]);

  const getStatus = useCallback((slug, variantLabel) => {
    const rec = invMap[keyFor(slug, variantLabel)];
    return rec ? rec.status : 'IN_STOCK';
  }, [invMap]);

  const getRecord = useCallback((slug, variantLabel) => {
    return invMap[keyFor(slug, variantLabel)] || emptyRec;
  }, [invMap]);

  // Admin: set stock and/or status. Requires bearer token in localStorage.
  const adminSet = useCallback(async (slug, variantLabel, { stock, status }) => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) throw new Error('Not authenticated');
    const body = { variant_label: variantLabel || null };
    if (typeof stock === 'number') body.stock = stock;
    if (status) body.status = status;
    const res = await fetch(`${API}/api/admin/inventory/${encodeURIComponent(slug)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new Error('Session expired');
    if (!res.ok) throw new Error(`Update failed (${res.status})`);
    const rec = await res.json();
    const k = keyFor(rec.product_slug, rec.variant_label);
    setInvMap(m => ({ ...m, [k]: { stock: rec.stock, status: rec.status } }));
    return rec;
  }, []);

  // Called after a successful order to update local map (backend already decremented).
  const applyLocalDecrement = useCallback((lines) => {
    setInvMap(prev => {
      const next = { ...prev };
      for (const line of lines) {
        const k = keyFor(line.slug, line.variant_label || null);
        const cur = next[k];
        if (!cur) continue;
        if (cur.status === 'IN_STOCK') {
          const newStock = Math.max(0, cur.stock - line.qty);
          next[k] = { stock: newStock, status: newStock === 0 ? 'OUT_OF_STOCK' : 'IN_STOCK' };
        } else if (cur.status === 'PREORDER') {
          next[k] = { ...cur, stock: Math.max(0, cur.stock - line.qty) };
        }
      }
      return next;
    });
  }, []);

  return (
    <InventoryContext.Provider value={{
      invMap, loading, refresh,
      getStock, getStatus, getRecord,
      adminSet, applyLocalDecrement,
    }}>
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => useContext(InventoryContext);
